import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Controller, useForm } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { useWealthboxDomainCommand } from '../../../../../api/wealthbox'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydDatePicker from '../../../../commonDesign/SydDatePicker'
import SydTextArea from '../../../../commonDesign/SydTextArea'
import { fetchTaskCategories } from './wealthboxQueryHelpers'
import { extractWealthboxErrorMessage } from './wealthboxErrors'

// Wealthbox `priority` is a string enum. None can't be set on create per docs
// (only Low/Medium/High); to "create without a priority" we just omit the
// field from the body. See PRIORITY_OPTIONS in WealthboxTaskDetailModal for
// the parallel set used by the edit modal.
const PRIORITY_OPTIONS = [
  { label: '(None)', value: '' },
  { label: 'High', value: 'High' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Low', value: 'Low' }
]

const CREATE_DEFAULTS = {
  name: '',
  // Default due date = today so the user doesn't have to fill an empty field
  // for the common "I'm noting something to do today/tomorrow" case.
  due_date: dayjs().format('YYYY-MM-DD'),
  description: '',
  priority: '',
  category: ''
}

function CreateWealthboxTaskModal ({
  open,
  onClose,
  scope = 'client',
  contactId,
  formMode = 'simple',
  invalidateQueryKeys
}) {
  const { clientId } = useAppContext()
  const isCalendar = formMode === 'calendar'
  const [categoryOptions, setCategoryOptions] = useState([])
  const [submitError, setSubmitError] = useState(null)

  const createTask = useWealthboxDomainCommand({ invalidateQueryKeys })

  const { control, handleSubmit, reset } = useForm({ defaultValues: CREATE_DEFAULTS })

  // Lazy-load task categories only in calendar form mode (matches the edit
  // modal). Reset when re-opening so a stale list never lingers across a
  // category-add in Wealthbox web UI.
  useEffect(() => {
    if (!open || !isCalendar) return
    let cancelled = false
    fetchTaskCategories({ clientId })
      .then(opts => { if (!cancelled) setCategoryOptions(opts) })
      .catch(() => { /* non-critical */ })
    return () => { cancelled = true }
  }, [open, isCalendar, clientId])

  // Reset form to defaults whenever the modal opens. Prevents stale values
  // from a previous create from showing on a fresh "+ Create" click.
  useEffect(() => {
    if (open) {
      reset(CREATE_DEFAULTS)
      setSubmitError(null)
    }
  }, [open, reset])

  const submit = handleSubmit(async (values) => {
    setSubmitError(null)
    // Build the body: only include fields the user filled in. Wealthbox
    // requires `name` and `due_date`; everything else is optional. For Client
    // scope, attach the linked contact via the body-shape `linked_to: [{id,
    // type}]` array (per Wealthbox POST docs - see SMT-1355 planning notes).
    // For My Book scope, the server-side `applyCreateScope` auto-fills
    // `assigned_to` from the resolved token.
    const body = {
      name: values.name?.trim(),
      due_date: values.due_date
    }
    if (values.description?.trim()) body.description = values.description.trim()
    if (values.priority) body.priority = values.priority
    if (values.category !== '' && values.category != null) body.category = Number(values.category)
    if (scope === 'client' && contactId != null) {
      body.linked_to = [{ id: Number(contactId), type: 'Contact' }]
    }

    try {
      await createTask.mutateAsync({
        command: 'createTask',
        request: { body, scope }
      })
      onClose()
    } catch (err) {
      setSubmitError(extractWealthboxErrorMessage(err, 'Failed to create task'))
    }
  })

  return (
    <SydModal open={open} onClose={onClose} title='Create Task' size='xmedium'>
      <form onSubmit={submit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={isCalendar ? 8 : 9}>
            <Controller
              name='name'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SydLabel label='Task Name' required>
                  <SydInput {...field} value={field.value || ''} placeholder='Task name' />
                </SydLabel>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={isCalendar ? 4 : 3}>
            <Controller
              name='due_date'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SydLabel label='Due Date' required>
                  <SydDatePicker
                    type='date'
                    dateFormat='YYYY-MM-DD'
                    value={field.value || null}
                    onChange={(d) => field.onChange(d ? dayjs(d).format('YYYY-MM-DD') : '')}
                  />
                </SydLabel>
              )}
            />
          </Grid>

          {isCalendar ? (
            <>
              <Grid item xs={12} sm={4}>
                <Controller
                  name='priority'
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='Priority'>
                      <SydSelect
                        options={PRIORITY_OPTIONS}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Controller
                  name='category'
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='Category'>
                      <SydSelect
                        options={[
                          { label: '(None)', value: '' },
                          ...categoryOptions
                        ]}
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value
                          field.onChange(raw === '' ? '' : Number(raw))
                        }}
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name='description'
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='Description'>
                      <SydTextArea
                        {...field}
                        value={field.value || ''}
                        rows={4}
                        placeholder='Description'
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
            </>
          ) : null}
        </Grid>

        {submitError ? (
          <div className='font-size-xs mt-2' style={{ color: 'var(--color-decline-500)' }}>
            {submitError}
          </div>
        ) : null}

        <SydModalActions>
          <SydButton variant='ghost' size='sm' onClick={onClose} disabled={createTask.isPending} type='button'>
            Cancel
          </SydButton>
          <SydButton variant='primary' size='sm' processing={createTask.isPending} type='submit'>
            Create Task
          </SydButton>
        </SydModalActions>
      </form>
    </SydModal>
  )
}

CreateWealthboxTaskModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  scope: PropTypes.oneOf(['client', 'user', 'all']),
  contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  formMode: PropTypes.oneOf(['simple', 'calendar']),
  invalidateQueryKeys: PropTypes.arrayOf(PropTypes.array)
}

export default CreateWealthboxTaskModal
